import React, { useCallback, useState } from "react";
import { Slider } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import { TeamSide, YardConverter } from "@prestosports/prestostats-core/Common";
import { useTeamsContext } from "../../../../state/teams/context";
import { ColoredMarker } from "./FieldMarkersEditor";
import { teamRailColors } from "../FieldPositionPicker";
import fieldRulerStyles from "../../styles/FieldRuler.styles";
import zoneToGainEditorStyles from "../../styles/ZoneToGainEditor.styles";

// Spot markers (grey) vs the current target marker (red), on one ruler.
const SPOT_FILL = "#ECECEC";
const SPOT_TEXT = "#616161";
const TARGET_FILL = "#d32f2f";
const TARGET_TEXT = "#FFFFFF";

const convertTo = (value, max, reversed) => (reversed ? max - value : value);

const sortedUnique = (values) => [...new Set(values)].sort((a, b) => a - b);

const snapToNearest = (target, options) =>
	options.length
		? options.reduce((prev, curr) =>
				Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
		  )
		: target;

// One editable pentagon thumb (painted body) — used for both the draggable
// spots and the current target. Mirrors FieldPositionPicker's ThumbComponent
// geometry, swapping in the painted ColoredMarker.
const makeThumb = (fieldLength, reversed, fill, textColor, sideTitle) => {
	const Thumb = (props) => {
		// eslint-disable-next-line react/prop-types
		const { "aria-valuenow": value } = props;
		const yardConverter = new YardConverter(fieldLength);
		const { teamSide, yard } = yardConverter.convertFromFieldPosition(
			convertTo(value, fieldLength, reversed)
		);
		return (
			// eslint-disable-next-line react/jsx-props-no-spreading
			<span {...props}>
				<Box style={{ position: "absolute", bottom: 14 }}>
					<ColoredMarker
						title={sideTitle(teamSide)}
						subtitle={yard}
						fill={fill}
						textColor={textColor}
					/>
				</Box>
			</span>
		);
	};
	return Thumb;
};

// Single ruler with editable zone-to-gain spots (grey, reposition only) plus
// the editable current target (red). Two stacked MUI sliders share the track:
// the target slider overlays the spots slider with a click-through rail so both
// thumb sets stay independently draggable.
const ZoneToGainEditor = ({
	spots = [],
	target,
	fieldLength,
	reversed,
	onSpotsChange = () => {},
	onTargetChange = () => {}
}) => {
	const classes = zoneToGainEditorStyles();
	const theme = useTheme();
	const { state: teamState } = useTeamsContext();
	const awayTeam = teamState.awayTeam();
	const homeTeam = teamState.homeTeam();
	const railClasses = fieldRulerStyles(
		teamRailColors(homeTeam, awayTeam, reversed, theme)
	);

	const sideTitle = useCallback(
		(teamSide) =>
			teamSide === TeamSide.Home
				? homeTeam?.abbrName?.substring(0, 3) || TeamSide.Home
				: awayTeam?.abbrName?.substring(0, 3) || TeamSide.Visitor,
		[homeTeam, awayTeam]
	);

	const shownSpots = sortedUnique(spots).map((s) =>
		convertTo(s, fieldLength, reversed)
	);
	const shownTarget = convertTo(Number(target), fieldLength, reversed);

	const [spotsDraft, setSpotsDraft] = useState(null);
	const [targetDraft, setTargetDraft] = useState(null);

	const half = fieldLength / 2;
	const marks = [];
	for (let i = 0; i <= fieldLength; i += 10) {
		marks.push({ value: i, label: i < half ? i : fieldLength - i });
	}

	const spotThumb = useCallback(
		makeThumb(fieldLength, reversed, SPOT_FILL, SPOT_TEXT, sideTitle),
		[fieldLength, reversed, sideTitle]
	);
	const targetThumb = useCallback(
		makeThumb(fieldLength, reversed, TARGET_FILL, TARGET_TEXT, sideTitle),
		[fieldLength, reversed, sideTitle]
	);

	const commitSpots = (_, next) => {
		setSpotsDraft(null);
		const inField = (Array.isArray(next) ? next : [next]).map((v) =>
			convertTo(v, fieldLength, reversed)
		);
		onSpotsChange(sortedUnique(inField));
	};

	// Target snaps to the nearest spot or goal line on release.
	const commitTarget = (_, next) => {
		setTargetDraft(null);
		const value = convertTo(
			Array.isArray(next) ? next[0] : next,
			fieldLength,
			reversed
		);
		onTargetChange(
			snapToNearest(value, [0, ...sortedUnique(spots), fieldLength])
		);
	};

	return (
		<Box className={classes.editor}>
			{shownSpots.length > 0 && (
				<Slider
					key={shownSpots.join(",")}
					className={`${classes.ruler} ${railClasses.teamRail}`}
					min={0}
					max={fieldLength}
					step={1}
					track={false}
					marks={marks}
					valueLabelDisplay="off"
					value={spotsDraft || shownSpots}
					ThumbComponent={spotThumb}
					onChange={(_, v) => setSpotsDraft(Array.isArray(v) ? v : [v])}
					onChangeCommitted={commitSpots}
				/>
			)}
			<Box className={classes.targetOverlay}>
				<Slider
					className={`${classes.targetSlider} ${railClasses.hiddenRail}`}
					min={0}
					max={fieldLength}
					step={1}
					track={false}
					valueLabelDisplay="off"
					value={targetDraft ?? shownTarget}
					ThumbComponent={targetThumb}
					onChange={(_, v) => setTargetDraft(v)}
					onChangeCommitted={commitTarget}
				/>
			</Box>
		</Box>
	);
};

export default ZoneToGainEditor;
